<template>
  <div class="audio-format">
    <h2 class="audio-format-title audio-format-title--nowrap">
      音频素材: {{ selectedAudio.name }}
    </h2>
    <!-- <pre>{{ displaySelectedAudio }}</pre> -->
    <label-button text="更换素材" @click="changeAudioMaterial">
      素材设置
    </label-button>
    <LabelInput
      label="开始于（秒）"
      :time="true"
      v-model="displayStart"
      :step="0.01"
    />
    <LabelInput
      label="持续时长（秒）"
      :time="true"
      v-model="displayDuration"
      :step="0.01"
    />
    <label-slider
      label="音量"
      v-model="selectedAudio.volume"
      :useModel="true"
      :max="100"
      :step="1"
      percentage
    />
    <label-switch label="音量淡入" v-model="selectedAudio.fadeIn" />
    <label-input
      label="时长 (s)"
      :disabled="!selectedAudio.fadeIn"
      v-model="selectedAudio.fadeInDuration"
      :time="true"
      :min="500"
      :max="6e3"
    />
    <label-switch label="音量淡出" v-model="selectedAudio.fadeOut" />
    <label-input
      label="时长 (s)"
      :disabled="!selectedAudio.fadeOut"
      v-model="selectedAudio.fadeOutDuration"
      :time="true"
      :min="500"
      :max="6e3"
    />
    <label-switch
      label="循环"
      :value="selectedAudio.loop"
      @change="handleLoopChange"
    />
    <label-slider
      label="背景音量"
      v-model="selectedAudio.musicVolume"
      :useModel="true"
      :max="100"
      :step="1"
      percentage
      class="audio-format__slider"
    />
    <label-checkbox label="播放时关闭背景音乐" v-model="musicMuted" />
    <label-input
      v-model="selectedAudio.playbackRate"
      label="播放速率"
      :step="0.01"
      :min="0.1"
      :max="10"
    />
    <h2 class="audio-format-title audio-format-title--between">音频截取</h2>
    <audio-cropper
      :src="selectedAudio.src"
      :value="displayCrop"
      @change="handleCropChange"
    />
    <h4 class="audio-format__help-title">
      <a-icon
        theme="filled"
        type="info-circle"
        class="audio-format__help-icon"
      />
      提示:
    </h4>
    <ol class="audio-format__help">
      <li>拖动两边滑块，或直接输入音频的起始与结束时间进行截取。</li>
    </ol>
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import LabelInput from '../base/LabelInput';
import LabelButton from '../base/LabelButton';
import LabelSwitch from '../base/LabelSwitch';
import LabelSlider from '../base/LabelSlider';
import LabelCheckbox from '../base/LabelCheckbox';
import { clamp, getAudioDuration } from '../../utils';
import AudioCropper from './AudioCropper';

export default {
  components: { LabelInput, LabelButton, LabelSlider, LabelSwitch, AudioCropper, LabelCheckbox },
  computed: {
    ...mapGetters(['activeFragment']),
    ...mapState(['selectedAudioId', 'audios']),
    selectedAudio() {
      return this.audios[this.selectedAudioId];
    },
    displaySelectedAudio() {
      return JSON.stringify(this.selectedAudio, null, 4);
    },
    displayStart: {
      get() {
        return this.selectedAudio.start;
      },
      set(value) {
        this.$set(this.selectedAudio, 'start', clamp(value, 0, this.selectedAudio.duration - 400));
      }
    },
    displayDuration: {
      get() {
        return this.selectedAudio.duration;
      },
      set(value) {
        this.$set(this.selectedAudio, 'duration', Math.max(value, 400));
      }
    },
    actualPlaybackRate() {
      return clamp(+this.selectedAudio.playbackRate || 1, .1, 10);
    },
    displayCrop() {
      const { crop, loop, duration } = this.selectedAudio;
      const actualDuration = duration * this.actualPlaybackRate;
      if (crop) {
        if (loop) {
          return crop;
        }
        return [crop[0], crop[0] + actualDuration / 1e3];
      }
      return [0, actualDuration / 1e3];
    },
    musicMuted: {
      get() {
        return this.selectedAudio.musicVolume === 0;
      },
      set(value) {
        this.$set(this.selectedAudio, 'musicVolume', value ? 0 : 100);
      }
    }
  },
  methods: {
    changeAudioMaterial() {
      this.$store.dispatch('setAudioEditing', true);
    },
    handleCropChange({ start, end, total }) {
      if (this.selectedAudio.loop) {
        this.$set(this.selectedAudio, 'crop', [start, end]);
      } else {
        this.$set(this.selectedAudio, 'crop', [start, total]);
        const duration = (end - start) * 1e3 / this.actualPlaybackRate;
        this.$set(this.selectedAudio, 'duration', clamp(duration, 400, this.activeFragment.duration - this.selectedAudio.start));
      }
    },
    async handleLoopChange(val) {
      if (val) {
        this.$set(this.selectedAudio, 'crop', [].concat(this.displayCrop));
        this.$set(this.selectedAudio, 'loop', true);
      } else {
        const duration = await getAudioDuration(this.selectedAudio.src);
        this.$set(this.selectedAudio, 'crop', [this.selectedAudio.crop[0], duration]);
        this.$set(this.selectedAudio, 'duration', clamp(
          (duration - this.selectedAudio.crop[0]) * 1e3 / this.actualPlaybackRate,
          400,
          this.selectedAudio.duration
        ));
        this.$set(this.selectedAudio, 'loop', false);
      }
    }
  },
  beforeDestroy() {
    this.$store.dispatch('setAudioEditing', false);
  },
  watch: {
    actualPlaybackRate(val) {
      const { crop, loop, duration: curDuration } = this.selectedAudio;
      if (crop && !loop) {
        const [start, end] = crop;
        const duration = (end - start) * 1e3 / val;
        if (duration < curDuration) {
          this.$set(this.selectedAudio, 'duration', clamp(duration, 400, this.activeFragment.duration - this.selectedAudio.start));
        }
      }
    }
  }
}
</script>
<style scoped>
.audio-format {
  padding: 16px;
  box-sizing: border-box;
  /* height: 100%; */
  display: flex;
  flex-flow: column;
}

.audio-format-title {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.audio-format-title--nowrap {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audio-format-title--between {
  margin-top: 16px;
}

.audio-format__slider {
  margin: 8px 0 0;
}

.audio-format__slider ::v-deep .label {
  width: auto;
}

.audio-format__slider ::v-deep .ant-slider {
  width: 80px;
}

.audio-format__help-title {
  margin: 20px 0 8px;
  display: flex;
  align-items: center;
  font-size: 12px;
}

.audio-format__help-icon {
  color: #faad14;
  margin-right: 8px;
  font-size: 14px;
}

.audio-format__help {
  padding-left: 14px;
  line-height: 1.8;
  margin: 0;
  font-size: 12px;
}

.audio-format__help ::v-deep li {
  padding-left: 4px;
}
</style>